// @ts-strict-ignore
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { UserContext } from '~/dashboard/context';
import apiCalls from '~/helpers/apiCalls';
import { typhonRoutes } from '~/helpers/routes';
import type { CustomQueryOpts } from '../@types/utils';

// ============================================================
//     TYPES
// ============================================================
type ApiResponse = {
  status_code: number;
  error: unknown | null;
  '_embedded': JourneyFederatedMetadata;
};

export type JourneyFederatedMetadata = {
  journey_token: string;
  parent_journey_token: string | null;
  is_federated: boolean;
  is_published: boolean;
  published_to_child_accounts: ChildOrgDetails[];
};

type ChildOrgDetails = {
  organization_name: string;
  organization_slug: string;
  is_controlled_child: boolean;
  journey_token?: string;
  federated_journey_active_version: number;
};

// ============================================================
//     QUERY
// ============================================================
export const queryKey = ({
  journeyToken,
}) => [typhonRoutes.embeddedFinance.getJourneyFederationStatus({ journeyToken })] as const;

/**
 * Get the AFEF federated status of a Journey
 */
export const useGetJourneyFederationStatusQuery = <
  TData extends JourneyFederatedMetadata,
  TErr,
  TSel = TData,
>(
    { journeyToken },
    options: CustomQueryOpts<TData, TErr, TSel> = {},
  ) => {
  const { typhonDomain } = useContext(UserContext);

  return useQuery(
    {
      queryKey: queryKey({ journeyToken }),
      queryFn: async () => {
        const response = await apiCalls.embeddedFinance.getJourneyFederationStatus({
          typhonDomain,
          journeyToken,
        }) as ApiResponse;

        if (response.status_code !== 200) {
          Promise.reject(response);
        }

        // eslint-disable-next-line no-underscore-dangle
        return response._embedded;
      },
      ...options,
    });
};

useGetJourneyFederationStatusQuery.queryKey = queryKey;
